import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Chevron = ({ open }) => (
  <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

// ── Build month → fund tree from transactions ────────────────────────────────

const buildTree = (tx) => {
  const months = Array.from({ length: 12 }, () => ({ income: 0, expense: 0, funds: {} }));

  tx.filter(t => t.type !== 'Starting Balance').forEach(t => {
    const mi = parseInt((t.stmtMonth || t.date || '').substring(5, 7), 10) - 1;
    if (mi < 0 || mi > 11) return;

    const add = (fund, inc, exp) => {
      months[mi].income  += inc;
      months[mi].expense += exp;
      if (!months[mi].funds[fund]) months[mi].funds[fund] = { income: 0, expense: 0 };
      months[mi].funds[fund].income  += inc;
      months[mi].funds[fund].expense += exp;
    };

    if (t.split && t.lines?.length) {
      t.lines.forEach(line => {
        const fund = line.classification || t.classification || '—';
        if (t.income  > 0) add(fund, line.amount || 0, 0);
        if (t.expense > 0) add(fund, 0, line.amount || 0);
      });
    } else {
      add(t.classification || '—', t.income || 0, t.expense || 0);
    }
  });

  return months;
};

// ── Shared amount columns ────────────────────────────────────────────────────

const AmountCols = ({ income, expense }) => {
  const net = income - expense;
  return (
    <>
      <td className="py-2 text-right text-green-600 font-mono text-xs w-24 px-2">
        {income > 0 ? fmt(income) : <span className="text-slate-500">—</span>}
      </td>
      <td className="py-2 text-right text-red-500 font-mono text-xs w-24 px-2">
        {expense > 0 ? fmt(expense) : <span className="text-slate-500">—</span>}
      </td>
      <td className={`py-2 text-right font-mono text-xs font-semibold w-24 px-2 ${net >= 0 ? 'text-slate-700' : 'text-red-500'}`}>
        {net >= 0 ? fmt(net) : `(${fmt(Math.abs(net))})`}
      </td>
    </>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

const IncomeExpense = () => {
  const orgId = useOrgId();
  const [txByYear, setTxByYear]         = useState({});
  const [expandedYears, setExpandedYears]   = useState({ [CURRENT_YEAR]: true });
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    if (!orgId) return;
    const unsubs = YEARS.map(year => {
      const q = query(collection(db, 'orgs', orgId, 'transactions'), where('year', '==', year));
      return onSnapshot(q, snap => {
        setTxByYear(prev => ({ ...prev, [year]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
      });
    });
    return () => unsubs.forEach(u => u());
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleYear  = (y)   => setExpandedYears(p  => ({ ...p, [y]:  !p[y]  }));
  const toggleMonth = (key) => setExpandedMonths(p => ({ ...p, [key]: !p[key] }));

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Cash Flow</h2>
        <p className="text-slate-500 text-sm mt-0.5">Expand a year or month to see details</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Column headers */}
        <div className="flex items-center bg-slate-700 text-white text-xs font-semibold px-4 py-2.5">
          <span className="flex-1">Period</span>
          <span className="w-24 text-right px-2">Money In</span>
          <span className="w-24 text-right px-2">Money Out</span>
          <span className="w-24 text-right px-2">Net</span>
        </div>

        <table className="w-full text-sm">
          <tbody>
            {YEARS.map(year => {
              const tx      = txByYear[year] || [];
              const months  = buildTree(tx);
              const yearInc = months.reduce((s, m) => s + m.income,  0);
              const yearExp = months.reduce((s, m) => s + m.expense, 0);
              const yearOpen = !!expandedYears[year];
              const hasData = yearInc > 0 || yearExp > 0;

              return (
                <React.Fragment key={year}>
                  {/* ── Year row ── */}
                  <tr
                    onClick={() => toggleYear(year)}
                    className="cursor-pointer border-b border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <td className="px-4 py-3 font-bold text-slate-800">
                      <div className="flex items-center gap-2">
                        <Chevron open={yearOpen} />
                        <span>{year}</span>
                        {!hasData && <span className="text-xs text-slate-500 font-normal">no data</span>}
                      </div>
                    </td>
                    <AmountCols income={yearInc} expense={yearExp} />
                  </tr>

                  {/* ── Months ── */}
                  {yearOpen && months.map((m, mi) => {
                    if (m.income === 0 && m.expense === 0) return null;
                    const mKey   = `${year}-${mi}`;
                    const mOpen  = !!expandedMonths[mKey];
                    const fundEntries = Object.entries(m.funds).filter(([, v]) => v.income > 0 || v.expense > 0);

                    return (
                      <React.Fragment key={mKey}>
                        {/* Month row */}
                        <tr
                          onClick={() => toggleMonth(mKey)}
                          className="cursor-pointer border-b border-gray-100 hover:bg-blue-50/40 transition-colors"
                        >
                          <td className="py-2.5 font-medium text-slate-700">
                            <div className="flex items-center gap-2 pl-8">
                              <Chevron open={mOpen} />
                              <span className="hidden sm:inline">{MONTH_NAMES[mi]}</span>
                              <span className="sm:hidden">{MONTH_SHORT[mi]}</span>
                            </div>
                          </td>
                          <AmountCols income={m.income} expense={m.expense} />
                        </tr>

                        {/* Fund rows */}
                        {mOpen && fundEntries.map(([fund, v]) => (
                          <tr key={fund} className="border-b border-gray-50 bg-white hover:bg-slate-50/60">
                            <td className="py-2 text-slate-500 text-xs">
                              <span className="pl-16 block truncate max-w-[140px] sm:max-w-none">{fund}</span>
                            </td>
                            <td className="py-2 text-right text-green-600 font-mono text-xs w-24 px-2">
                              {v.income > 0 ? fmt(v.income) : <span className="text-slate-500">—</span>}
                            </td>
                            <td className="py-2 text-right text-red-500 font-mono text-xs w-24 px-2">
                              {v.expense > 0 ? fmt(v.expense) : <span className="text-slate-500">—</span>}
                            </td>
                            <td className={`py-2 text-right font-mono text-xs w-24 px-2 ${(v.income - v.expense) >= 0 ? 'text-slate-500' : 'text-red-500'}`}>
                              {(() => { const n = v.income - v.expense; return n >= 0 ? fmt(n) : `(${fmt(Math.abs(n))})`; })()}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncomeExpense;
